import React, { useEffect, useState, useCallback } from "react";
import { useDebounce } from "use-debounce";
import api from "@/services/api";
import useStore from "@/services/store";
import { timeSince } from "@/utils";
import Loader from "@/components/loader";
import { useComponentTranslation } from "@/services/i18n";
import { FiClock, FiHeart, FiSearch, FiMapPin, FiBriefcase, FiFilter, FiX } from "react-icons/fi";
import FilterDropdown from "./filter-dropdown";

const translations = {
  en: {
    candidate_home: {
      hello: "Hello",
      welcome: "Welcome to your job search",
      subtitle: "here are the offers that match your profile",
      view_offer: "View offer",
      posted: "Posted",
      no_jobs: "No matching jobs found",
      ago: "ago",
      search_placeholder: "Search jobs by title...",
      filter_location: "Location",
      filter_contract: "Contract Type",
      filter_employment: "Employment Type",
      filter_clear: "Clear filters",
      filter_show: "Show filters",
      filter_hide: "Hide filters",
      contract_cdi: "CDI",
      contract_cdd: "CDD",
      contract_internship: "Internship",
      contract_apprentice: "Apprentice",
      contract_interim: "Interim",
      contract_freelancer: "Freelancer",
      employment_fulltime: "Full-time",
      employment_parttime: "Part-time",
    },
  },
  fr: {
    candidate_home: {
      hello: "Bonjour",
      welcome: "Bienvenue dans votre recherche d'emploi",
      subtitle: "voici les offres qui correspondent à votre profil",
      view_offer: "Voir l'offre",
      posted: "Publié il y a",
      no_jobs: "Aucune offre correspondante trouvée",
      ago: "il y a",
      search_placeholder: "Rechercher des emplois par titre...",
      filter_location: "Localisation",
      filter_contract: "Type de contrat",
      filter_employment: "Type d'emploi",
      filter_clear: "Effacer les filtres",
      filter_show: "Afficher les filtres",
      filter_hide: "Masquer les filtres",
      contract_cdi: "CDI",
      contract_cdd: "CDD",
      contract_internship: "Stage",
      contract_apprentice: "Apprentissage",
      contract_interim: "Intérim",
      contract_freelancer: "Freelance",
      employment_fulltime: "Temps plein",
      employment_parttime: "Temps partiel",
    },
  },
};

export default function CandidateHome() {
  const { t } = useComponentTranslation(translations);
  const { user } = useStore();
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [availableCities, setAvailableCities] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    city: "",
    contract: "",
    employment_type: "",
    page: 1,
    limit: 10,
  });
  const [searchValue] = useDebounce(filters.search, 500);

  // Extract cities from jobs
  useEffect(() => {
    if (jobs && Array.isArray(jobs)) {
      const cities = jobs.reduce((acc, job) => {
        if (job?.job_location && !acc.includes(job.job_location)) {
          acc.push(job.job_location);
        }
        return acc;
      }, []);
      setAvailableCities(cities.sort());
    }
  }, [jobs]);

  useEffect(() => {
    getOffers();
  }, [searchValue, filters.city, filters.contract, filters.employment_type, filters.page]);

  const getOffers = async () => {
    setLoading(true);
    const params = {
      limit: filters.limit,
      status: "active",
      page: filters.page,
      channel_name: "Jobego",
    };

    if (filters.search) params.search = filters.search;
    if (filters.city) params.city = filters.city;
    if (filters.contract) params.contract = filters.contract;
    if (filters.employment_type) params.employment_type = filters.employment_type;

    const { ok, data } = await api.post("/post/search", params);
    if (!ok) {
      setLoading(false);
      return;
    }
    setJobs(data);
    setLoading(false);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      city: "",
      contract: "",
      employment_type: "",
      page: 1,
      limit: 10,
    });
  };

  const contractOptions = [
    { value: "CDI", label: t("candidate_home.contract_cdi") },
    { value: "CDD", label: t("candidate_home.contract_cdd") },
    { value: "Internship", label: t("candidate_home.contract_internship") },
    { value: "Apprentice", label: t("candidate_home.contract_apprentice") },
    { value: "Interim", label: t("candidate_home.contract_interim") },
    { value: "Freelancer", label: t("candidate_home.contract_freelancer") },
  ];

  const employmentOptions = [
    { value: "Full-time", label: t("candidate_home.employment_fulltime") },
    { value: "Part-time", label: t("candidate_home.employment_parttime") },
  ];

  const locationOptions = availableCities.map((city) => ({ value: city, label: city }));

  const hasActiveFilters = filters.search || filters.city || filters.contract || filters.employment_type;

  return (
    <div className="">
      <h1 className="text-xl text-gray-700 mb-4">
        <strong>
          👋 {t("candidate_home.hello")} {user.firstname}
        </strong>
      </h1>

      {/* Search and Filters Section */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t("candidate_home.search_placeholder")}
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
              showFilters ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FiFilter className="w-5 h-5" />
            <span className="hidden sm:inline">{showFilters ? t("candidate_home.filter_hide") : t("candidate_home.filter_show")}</span>
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <FilterDropdown
                icon={<FiMapPin className="w-4 h-4" />}
                label={t("candidate_home.filter_location")}
                options={locationOptions}
                value={filters.city ? [filters.city] : []}
                onChange={(values) => setFilters((prev) => ({ ...prev, city: values[0] || "", page: 1 }))}
                multiSelect={false}
                placeholder={t("candidate_home.filter_location")}
                className="w-full sm:w-auto"
              />
              <FilterDropdown
                icon={<FiBriefcase className="w-4 h-4" />}
                label={t("candidate_home.filter_contract")}
                options={contractOptions}
                value={filters.contract ? [filters.contract] : []}
                onChange={(values) => setFilters((prev) => ({ ...prev, contract: values[0] || "", page: 1 }))}
                multiSelect={false}
                placeholder={t("candidate_home.filter_contract")}
                className="w-full sm:w-auto"
              />
              <FilterDropdown
                icon={<FiBriefcase className="w-4 h-4" />}
                label={t("candidate_home.filter_employment")}
                options={employmentOptions}
                value={filters.employment_type ? [filters.employment_type] : []}
                onChange={(values) => setFilters((prev) => ({ ...prev, employment_type: values[0] || "", page: 1 }))}
                multiSelect={false}
                placeholder={t("candidate_home.filter_employment")}
                className="w-full sm:w-auto"
              />
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <FiX className="w-4 h-4" />
                  {t("candidate_home.filter_clear")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Jobs Grid */}
      {loading ? (
        <div className="flex mt-20 justify-center">
          <Loader />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {jobs?.length > 0 ? (
            jobs.map((job) => <JobCard key={job._id} job={job} />)
          ) : (
            <div className="text-center py-12 text-gray-500 col-span-full">{t("candidate_home.no_jobs")}</div>
          )}
        </div>
      )}
    </div>
  );
}

const JobCard = ({ job }) => {
  const { t } = useComponentTranslation(translations);
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
    // TODO: add like to job
  };

  // Construct the external link for viewing the offer
  const viewOfferUrl = `https://jobego.co/companies/${job.organization_slug}/jobs/${job._id}`;

  return (
    <a
      href={viewOfferUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
    >
      {/* Content Section */}
      <div className="flex-1 px-5 py-4 flex flex-col">
        {/* Header */}
        <div className="mb-3 relative">
          <button onClick={handleLike} className="absolute top-0 right-0 p-2 hover:bg-gray-100 rounded-full transition-all duration-200 z-10" aria-label="Save job">
            <FiHeart className={`w-5 h-5 transition-colors ${isLiked ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-500"}`} />
          </button>
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-1.5 pr-10">{job.job_title}</h3>
          <p className="text-sm text-gray-600 font-medium truncate">{job.organization_name}</p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {job.job_location && <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">{job.job_location}</span>}
          {job.job_contract_type && <span className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">{job.job_contract_type}</span>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <FiClock className="w-3.5 h-3.5" />
            {t("candidate_home.posted")} {timeSince(job.created_at)} {t("candidate_home.ago")}
          </span>
          <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium group-hover:bg-blue-700 transition-colors shadow-sm">
            {t("candidate_home.view_offer")}
            <svg className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </a>
  );
};
